/* swt-tech-wars-mystery.js · Homepage V2 · Tech Wars + Swift Pick "Mystery mode"
   v2 (11-02-26): timer unlock. v1 (10-28-26) only showed "Refresh to see it" at zero.
   <swt-tw-reveal data-reveal="unix seconds" data-countdown="true|false">
   - Counts down to the reveal on the visitor's clock (once a second).
   - At zero it unlocks the section in place, with no page reload: it asks Shopify for a
     fresh copy of just this section (Section Rendering API, ?section_id=, plus a unique
     param so no cached copy is served), and swaps the blur for the real product.
   - The server decides the reveal, so nothing about the product is in the page before
     then. If the server still says "locked" (the visitor's clock runs a little fast),
     it tries again a few times over about 2 minutes. If it never unlocks, the
     "Refresh to see it" line stays as the fallback.
   - Page served after the reveal (a cached copy): same in-place unlock straight away.
     If the network request fails, it falls back to one reload with ?tw_reveal=<time>,
     guarded by sessionStorage so it can never loop.
   - Never runs in the theme editor (use the block's "preview revealed" box there).
   - Scripts and stylesheets the revealed section needs are loaded before the swap, so it
     never shows unstyled. Fade-in skipped for reduced motion. */
(function () {
  if (customElements.get('swt-tw-reveal')) return;

  var RETRY_MS = [0, 2000, 5000, 10000, 20000, 40000, 60000];
  var busy = {};

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function format(ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    var sec = s % 60;
    if (d > 0) return d + 'd ' + pad(h) + 'h ' + pad(m) + 'm';
    return pad(h) + 'h ' + pad(m) + 'm ' + pad(sec) + 's';
  }

  function words(ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    var out = [];
    if (d) out.push(d + (d === 1 ? ' day' : ' days'));
    if (h) out.push(h + (h === 1 ? ' hour' : ' hours'));
    out.push(m + (m === 1 ? ' minute' : ' minutes'));
    return out.join(', ');
  }

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  /* Ask Shopify for a fresh render of one section. Resolves to its wrapper element. */
  function fetchSection(id) {
    var url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('tw_reveal');
    url.searchParams.set('section_id', id);
    url.searchParams.set('tw_unlock', String(Date.now()));
    return fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrap = doc.getElementById('shopify-section-' + id);
        if (!wrap) throw new Error('section missing');
        return wrap;
      });
  }

  function loadedStylesheets() {
    var set = {};
    document.querySelectorAll('link[rel="stylesheet"]').forEach(function (l) {
      if (l.href && l.media !== 'print') set[l.href] = true;
    });
    return set;
  }

  /* Load any stylesheet the fresh section needs that the page doesn't have yet. */
  function prepareStyles(fresh) {
    var have = loadedStylesheets();
    var jobs = [];
    fresh.querySelectorAll('link[rel="stylesheet"]').forEach(function (l) {
      var href = new URL(l.getAttribute('href'), window.location.href).toString();
      if (!have[href]) {
        jobs.push(new Promise(function (resolve) {
          var pre = document.createElement('link');
          pre.rel = 'stylesheet';
          pre.href = href;
          pre.onload = pre.onerror = resolve;
          document.head.appendChild(pre);
          window.setTimeout(resolve, 3000);
        }));
      }
      /* Already loaded (or loading above): show it at once, no print/onload swap. */
      l.media = 'all';
      l.removeAttribute('onload');
    });
    return Promise.all(jobs);
  }

  /* Scripts inside fetched HTML don't run on their own. Add the missing ones. */
  function runScripts(fresh, hadSrc) {
    fresh.querySelectorAll('script').forEach(function (old) {
      var type = (old.getAttribute('type') || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'module') return;   /* JSON data etc. */
      var src = old.getAttribute('src');
      if (src) {
        var abs = new URL(src, window.location.href).toString();
        if (hadSrc[abs]) return;
        var s = document.createElement('script');
        s.src = abs;
        s.async = false;
        if (type === 'module') s.type = 'module';
        document.head.appendChild(s);
        hadSrc[abs] = true;
      } else {
        var inline = document.createElement('script');
        if (type === 'module') inline.type = 'module';
        inline.textContent = old.textContent;
        old.replaceWith(inline);
      }
    });
  }

  function swapIn(wrap, fresh) {
    var hadSrc = {};
    document.querySelectorAll('script[src]').forEach(function (s) { hadSrc[s.src] = true; });
    var hadFocus = wrap.contains(document.activeElement);

    wrap.replaceWith(fresh);
    runScripts(fresh, hadSrc);

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && fresh.animate) {
      fresh.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 450, easing: 'ease-out' });
    }
    if (hadFocus) {
      var h = fresh.querySelector('h2, h3');
      if (h) {
        h.setAttribute('tabindex', '-1');
        h.focus({ preventScroll: true });
      }
    }
    document.dispatchEvent(new CustomEvent('swt:reveal:unlocked', { detail: { sectionId: fresh.id } }));
  }

  /* Unlock one section in place. Resolves true when swapped, false when it gave up. */
  function unlock(wrap) {
    var id = wrap.id.replace(/^shopify-section-/, '');
    if (busy[id]) return busy[id];
    wrap.setAttribute('aria-busy', 'true');

    var attempt = 0;
    function next() {
      if (attempt >= RETRY_MS.length) {
        wrap.removeAttribute('aria-busy');
        return Promise.resolve(false);
      }
      var delay = RETRY_MS[attempt];
      attempt += 1;
      return wait(delay)
        .then(function () { return fetchSection(id); })
        .then(function (fresh) {
          /* Server still rendering the mystery = its clock hasn't passed the reveal yet */
          if (fresh.querySelector('swt-tw-reveal[data-reveal]')) return next();
          return prepareStyles(fresh).then(function () {
            swapIn(wrap, fresh);
            return true;
          });
        });
    }

    busy[id] = next().catch(function (err) {
      wrap.removeAttribute('aria-busy');
      delete busy[id];
      throw err;
    });
    return busy[id];
  }

  class SwtTwReveal extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.reveal = (parseInt(this.dataset.reveal, 10) || 0) * 1000;
      if (!this.reveal) return;
      this.clock = this.querySelector('[data-tw-clock]');
      this.label = this.querySelector('[data-tw-count-label]');
      this.live = this.querySelector('[data-tw-live]');
      this.showClock = this.dataset.countdown === 'true' && this.clock;
      this.editor = !!(window.Shopify && window.Shopify.designMode);

      if (Date.now() >= this.reveal) {
        this.done(true);
        return;
      }
      if (this.showClock) {
        this.clock.hidden = false;
        if (this.label) this.label.hidden = false;
      }
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }

    tick() {
      var left = this.reveal - Date.now();
      if (left <= 0) {
        this.done(false);
        return;
      }
      if (this.showClock) {
        var text = format(left);
        if (this.clock.textContent !== text) this.clock.textContent = text;
        this.clock.setAttribute('aria-label', 'Revealed in ' + words(left));
      }
    }

    done(arrivedLate) {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
      if (this.clock) this.clock.hidden = true;
      if (this.label) this.label.hidden = true;
      if (this.live) this.live.hidden = false;   /* "Refresh to see it" = fallback while unlocking */
      if (this.editor) return;

      var wrap = this.closest('.shopify-section');
      var reveal = this.reveal;
      if (!wrap || !window.fetch || !window.DOMParser) {
        if (arrivedLate) reloadOnce(reveal);
        return;
      }
      unlock(wrap).catch(function () {
        /* Network or server error: a page that arrived late gets one fresh reload */
        if (arrivedLate) reloadOnce(reveal);
      });
    }
  }

  function reloadOnce(revealMs) {
    var key = 'swt-tw-reveal-' + revealMs;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, '1');
    } catch (e) {
      return;   /* no storage = no automatic reload; the link still works */
    }
    var url = new URL(window.location.href);
    url.searchParams.set('tw_reveal', String(revealMs / 1000));
    window.location.replace(url.toString());
  }

  customElements.define('swt-tw-reveal', SwtTwReveal);
})();
